/**
 * Merina HMS - Accessibility Root Injector
 * ==========================================
 * Injects accessibility layer into DOM without modifying existing components.
 * Creates separate #accessibility-root at body level.
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import AccessibilityAgent from './AccessibilityAgent';
import accessibilityEngine from './AccessibilityEngine';
import domEnhancer from './DOMEnhancer';

// Unique ID for accessibility root
const ACCESSIBILITY_ROOT_ID = 'accessibility-root';

/**
 * Initialize the accessibility layer
 * Called once when the app loads
 */
export function initAccessibility() {
  // Check if already initialized
  if (document.getElementById(ACCESSIBILITY_ROOT_ID)) {
    console.log('[Accessibility] Already initialized');
    return;
  }

  // Create accessibility root container
  const accessibilityRoot = document.createElement('div');
  accessibilityRoot.id = ACCESSIBILITY_ROOT_ID;
  accessibilityRoot.setAttribute('aria-hidden', 'false');
  accessibilityRoot.setAttribute('data-accessibility-layer', 'true');
  
  // Append to body (after app root)
  document.body.appendChild(accessibilityRoot);

  // Initialize the accessibility engine (applies saved settings)
  accessibilityEngine.init();

  // Initialize DOM enhancer (adds ARIA labels, roles, etc.)
  domEnhancer.init();

  // Create React root for accessibility UI
  const root = createRoot(accessibilityRoot);
  root.render(
    <React.StrictMode>
      <AccessibilityAgent />
    </React.StrictMode>
  );

  // Add skip link for keyboard users (at the very top of body)
  addSkipLink();

  // Add live region for announcements
  addLiveRegion();

  console.log('[Accessibility] Layer initialized successfully');
}

/**
 * Add skip-to-content link for keyboard navigation
 */
function addSkipLink() {
  if (document.querySelector('.a11y-skip-link')) return;

  const skipLink = document.createElement('a');
  skipLink.href = '#main-content';
  skipLink.className = 'a11y-skip-link';
  skipLink.textContent = 'Skip to main content';
  skipLink.style.cssText = `
    position: fixed;
    top: -100px;
    left: 50%;
    transform: translateX(-50%);
    background: #6366f1;
    color: white;
    padding: 12px 24px;
    border-radius: 0 0 8px 8px;
    z-index: 100000;
    text-decoration: none;
    font-weight: 500;
    transition: top 0.2s;
  `;

  skipLink.addEventListener('focus', () => {
    skipLink.style.top = '0';
  });

  skipLink.addEventListener('blur', () => {
    skipLink.style.top = '-100px';
  });

  // Insert at the very beginning of body
  document.body.insertBefore(skipLink, document.body.firstChild);

  // Ensure main content has ID
  const mainContent = document.querySelector('main, [role="main"], #root > div');
  if (mainContent && !mainContent.id) {
    mainContent.id = 'main-content';
  }
}

/**
 * Add ARIA live region for dynamic announcements
 */
function addLiveRegion() {
  if (document.getElementById('a11y-announcer')) return;

  const liveRegion = document.createElement('div');
  liveRegion.id = 'a11y-announcer';
  liveRegion.setAttribute('aria-live', 'polite');
  liveRegion.setAttribute('aria-atomic', 'true');
  liveRegion.className = 'sr-only';
  liveRegion.style.cssText = `
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  `;

  document.body.appendChild(liveRegion);
}

/**
 * Announce a message to screen readers
 */
export function announce(message, priority = 'polite') {
  const announcer = document.getElementById('a11y-announcer');
  if (announcer) {
    announcer.setAttribute('aria-live', priority);
    announcer.textContent = message;
    
    // Clear after announcement
    setTimeout(() => {
      announcer.textContent = '';
    }, 1000);
  }
}

/**
 * Get current accessibility settings
 */
export function getAccessibilitySettings() {
  return accessibilityEngine.getSettings();
}

/**
 * Update an accessibility setting programmatically
 */
export function setAccessibilitySetting(key, value) {
  accessibilityEngine.setSetting(key, value);
}

/**
 * Reset all accessibility settings
 */
export function resetAccessibilitySettings() {
  accessibilityEngine.resetSettings();
}

export default initAccessibility;
